<template>
  <div class="category-news__wrapper">
    <a-lazy-hydrate when-visible>
      <a-breadcrumbs :settings="breadcrumbsSettings" :prettify-names="false" />
    </a-lazy-hydrate>
    <a-visibility hide :on="[$breakpoint.mobile]">
      <h1 class="category-news__title title">
        {{ categoryName }}
      </h1>
    </a-visibility>
    <div class="category-news__container">
      <a-visibility show :on="[$breakpoint.mobile]">
        <h1 class="category-news__title">
          {{ categoryName }}
        </h1>
      </a-visibility>
      <a-article-list :articles="articles" class="category-news__article-list">
        <template v-slot="{ article, position }">
          <a-lazy-hydrate
            when-visible
            :key="
              $_lazyHydratedComponentState_getHydratedComponentRefreshKey(
                article.articleId
              )
            "
          >
            <a-article-slot
              v-bind="article"
              :show-expandable-content="true"
              :image-viewport-optimization="isInViewport(position)"
              :hide-category="isArticleCategoryHidden(article)"
              class="category-news__article"
              @mounted="
                $_lazyHydratedComponentState_onHydratedComponentMounted(
                  article.articleId
                )
              "
            />
          </a-lazy-hydrate>
        </template>

        <template v-slot:banner="{ position }">
          <a-video-banner
            v-if="isVideoBannerVisible(position)"
            :video-banner-type="VIDEO_BANNER_TYPE.CATEGORY"
            class="category-news__video-banner"
          />
          <a-visibility
            v-else-if="isBannerVisible(position)"
            show
            :on="[$breakpoint.mobile]"
          >
            <a-banner
              :banner-settings="getBannerSettings(position)"
              :wrapped-in-card="true"
              class="category-news__banner"
            />
          </a-visibility>
        </template>

        <template v-slot:video="{ position }">
          <a-video-slot
            v-if="isVideoVisible(position)"
            :key="
              $_lazyHydratedComponentState_getHydratedComponentRefreshKey(
                videoSettings.Video.ExternalId
              )
            "
            :video-settings="videoSettings"
            class="category-news__video"
            @mounted="
              $_lazyHydratedComponentState_onHydratedComponentMounted(
                videoSettings.Video.ExternalId
              )
            "
          />
        </template>
      </a-article-list>

      <a-view-more
        :page="pagination.page"
        :pages-count="pagesCount"
        :in-progress="isRequestInProgress"
        class="category-news__view-more"
        :view-more-style="$options.consts.VIEW_MORE_STYLE.BUTTON"
        @click="getNextArticleChunk"
      />
    </div>

    <a-url-pagination
      :pagination.sync="pagination"
      :item-count="articles.length"
      :initial-page="initialPage"
      :item-selector="ARTICLE_SLOT_SELECTOR"
      :pages-count="pagesCount"
      :default-take-fn="getDefaultTake"
      :non-paginated-url="canonicalUrlWithoutPagination"
    />

    <a-pagination-links-for-google
      :page="pagination.page"
      :pages-count="pagesCount"
      :non-paginated-url="canonicalUrlWithoutPagination"
    />

    <a-refresh-listener
      :articles.sync="articles"
      :feed-refresh-in-progress.sync="feedRefreshInProgress"
      :slot-refresh-in-progress.sync="slotRefreshInProgress"
      :fetch-data-fn="fetchLoadedArticles"
      :target-category-slug="categorySlug"
      :settings="$options.consts.AUTO_REFRESH_SETTINGS.CATEGORY_PAGE"
      @feed-refreshed="$_disqusCommentCount_updateCommentCounts"
    />
  </div>
</template>

<script>
import { mapActions } from 'vuex'
import mixins from '@/utils/mixins'
import AArticleList from 'shared/AArticleList'
import ABanner from 'shared/ABanner'
import {
  BANNER_SETTINGS,
  getInsideListMpuBannerSettingsKey
} from 'enums/banners/banner-settings'
import { aspectRatios } from 'enums/aspectRatios'
import AUrlPagination from 'shared/AUrlPagination'
import ARefreshListener from 'shared/ARefreshListener'
import { SPINNER_ALIGNMENT } from 'shared/AInProgress'
import { addEndingSlash } from '@fmpedia/helpers'
import { createPrefilledArray, getPageNumberFromRoute } from '@/plugins/helper'
import { generateCategoryPath } from '@/utils/helpers/url-generators'
import { ROUTE_NAME } from 'enums/routes'
import { findMigratedCategorySlug } from 'enums/categories'
import {
  generatePaginatedCanonicalUrl,
  checkIfNonCanonicalPath
} from '@/utils/helpers/canonical-links'
import { STICKY_BANNER_POSITION } from 'enums/banners/sticky-banner'
import { VIDEO_BANNER_TYPE } from 'enums/video-banner'
import { PAGE_SCHEME_TYPE } from 'enums/pageSchemes'
import { DISQUS_SOURCE } from 'shared/AActionIcon/_shared/CommentCount/enums'
import APaginationLinksForGoogle from 'shared/APaginationLinksForGoogle'
import { AUTO_REFRESH_SETTINGS } from 'enums/article-auto-refresh'
import { hydrationHelpers } from '@/utils/mixins/hydrationHelpers'
import { hydrateWhenVisible } from '@/utils/helpers/vue-lazy-hydration/LazyHydrate'
import { redirectHandler } from '@/utils/helpers/redirects/helpers'
import { throwPageNotFoundError } from '@/plugins/error'
import AArticleSlot from 'shared/AArticleSlot/index.vue'
import AViewMore from 'shared/AViewMore/index.vue'
import { VIEW_MORE_STYLE } from 'shared/AViewMore/enums'
import { pick } from 'ramda'

const DEFAULT_TAKE = 25
const VIDEO_BANNER_POSITION = 2
const BANNER_POSITIONS = [6, 9]
const VIDEO_POSITION = 5
const ARTICLE_SLOT_SELECTOR = '.category-news__article'
const ARTICLE_SLOT_COUNT_IN_VIEWPORT = 2

function generateDefaultFeedContentDataModel() {
  return {
    articles: [],
    videoSettings: null,
    pagesCount: 0
  }
}

async function getFeedData({ store, route }) {
  const { dispatch } = store
  const initialPage = getPageNumberFromRoute(route)
  const { category: categorySlug } = route.params

  const pagination = {
    defaultTake: DEFAULT_TAKE,
    page: initialPage
  }

  const payload = {
    categorySlug,
    ...pagination
  }

  const requests = [
    dispatch('news/requestNewsByCategory', payload),
    dispatch('categories/requestCategoryVideo', { categorySlug })
  ]

  const [
    {
      articles,
      categoryName,
      categorySlug: categorySlugFromBackend,
      pagesCount
    },
    videoSettings
  ] = await Promise.all(requests)

  return {
    articles,
    videoSettings,
    categoryName,
    categorySlug,
    categorySlugFromBackend,
    initialPage,
    pagination,
    pagesCount
  }
}

export default {
  name: 'CategoryPage',
  scrollToTop: true,
  components: {
    AViewMore,
    AArticleSlot,
    AVideoBanner: hydrateWhenVisible(() => import('shared/AVideoBanner'), {
      props: ['video-banner-type']
    }),
    AArticleList,
    ABanner,
    AVideoSlot: hydrateWhenVisible(() => import('shared/AVideoSlot'), {
      props: ['video-settings']
    }),
    AUrlPagination,
    ARefreshListener,
    APaginationLinksForGoogle
  },
  mixins: [
    mixins.setPageLoaded,
    mixins.urlGenerators,
    mixins.articleDataHandler,
    mixins.ebookDataFetchMixinFactory(),
    mixins.disqusCommentCountMixinFactory({ source: DISQUS_SOURCE.IL }),
    hydrationHelpers,
    mixins.delayedContentRefresh,
    mixins.lazyHydratedComponentState
  ],
  consts: { AUTO_REFRESH_SETTINGS, VIEW_MORE_STYLE },
  head() {
    return this.$generateMetaTags({
      titleParam: this.categoryName,
      descriptionParam: this.categoryName,
      canonicalUrl: this.canonicalUrl,
      schemes: [
        { type: PAGE_SCHEME_TYPE.ORGANIZATION },
        { type: PAGE_SCHEME_TYPE.WEB_SITE },
        ...this.$helper.generateVideoSchemasFromResponse([
          this.videoSettings?.Video,
          ...this.articles.map(article => article.video)
        ])
      ]
    })
  },
  async asyncData(ctx) {
    const { path: currentPath, fullPath } = ctx.route

    try {
      const data = await getFeedData({ store: ctx.store, route: ctx.route })

      if (data.pagesCount && data.initialPage > data.pagesCount - 1) {
        return throwPageNotFoundError(ctx, 'CategoryPage')
      }

      const categoryUrl = generateCategoryPath({
        slug: data.categorySlugFromBackend
      })
      const isMigratedCategory = !!findMigratedCategorySlug(data.categorySlug)
      const handler = isMigratedCategory ? v => v : addEndingSlash

      const canonicalUrlWithoutPagination = handler(
        `${ctx.$env.IL_DOMAIN_URL}${categoryUrl}`
      )
      const canonicalUrl = generatePaginatedCanonicalUrl({
        canonicalUrlWithoutPagination,
        path: currentPath
      })

      if (
        checkIfNonCanonicalPath(ctx, {
          currentPath,
          canonicalUrl
        })
      ) {
        redirectHandler({ ctx, to: canonicalUrl })
        return
      }

      return {
        ...data,
        canonicalUrl,
        canonicalUrlWithoutPagination,
        feedRefreshInProgress: false,
        slotRefreshInProgress: createPrefilledArray(data.articles.length, false)
      }
    } catch (err) {
      ctx.app.$errorHandler(err, ctx, {
        showErrorPage: true,
        componentName: 'CategoryPage'
      })
    } finally {
      await ctx.store.dispatch('router/removeRouteFromQueue', fullPath)
    }
  },
  data() {
    const initialPage = getPageNumberFromRoute(this.$route)

    return {
      ...generateDefaultFeedContentDataModel(),
      canonicalUrl: this.$route.path,
      canonicalUrlWithoutPagination: this.$route.path,
      BANNER_SETTINGS,
      ARTICLE_SLOT_SELECTOR,
      SPINNER_ALIGNMENT,
      aspectRatios,
      initialPage,
      pagination: {
        defaultTake: DEFAULT_TAKE,
        page: initialPage
      },
      inProgress: false,
      articles: [],
      videoSettings: null,
      categoryName: '',
      categorySlug: '',
      feedRefreshInProgress: false,
      slotRefreshInProgress: [],
      STICKY_BANNER_POSITION,
      VIDEO_BANNER_TYPE
    }
  },
  computed: {
    isRequestInProgress() {
      return this.inProgress || this.isPendingContentRefresh
    },
    breadcrumbsSettings() {
      return {
        [ROUTE_NAME.CATEGORY]: { nameHandler: () => this.categoryName }
      }
    }
  },
  methods: {
    ...mapActions({
      requestNewsByCategory: 'news/requestNewsByCategory'
    }),
    isArticleCategoryHidden(article) {
      return (
        !article.sponsorshipCompany ||
        article.category.slug === this.$route.params.category
      )
    },
    async getCategoryNews() {
      try {
        this.inProgress = true
        const payload = {
          categorySlug: this.categorySlug,
          ...this.pagination
        }

        const {
          articles,
          categoryName,
          pagesCount
        } = await this.requestNewsByCategory(payload)

        this.articles = [...this.articles, ...articles]
        this.slotRefreshInProgress = createPrefilledArray(
          this.articles.length,
          false
        )
        this.categoryName = categoryName
        this.pagesCount = pagesCount
      } catch (err) {
        this.$errorHandler(err, this)
      } finally {
        this.inProgress = false
      }
    },
    getNextArticleChunk() {
      this.pagination.page++
      this.getCategoryNews()
    },
    isVideoBannerVisible(index) {
      const position = index + 1
      return position === VIDEO_BANNER_POSITION
    },
    /**
     * More details regarding the rotation of banners on this page here:
     * https://docs.google.com/document/d/1kDCjTGJJkBU-EGjYPcno1ZrRfVFx4bTk/edit#heading=h.tyjcwt
     * The spec is for All News, but the rule is applicable to this page as well
     */
    isBannerVisible(index) {
      return BANNER_POSITIONS.includes(index + 1)
    },
    getBannerSettings(index) {
      if (!this.isBannerVisible(index)) return null

      const bannerPosition = index + 1
      const bannerNumber = BANNER_POSITIONS.indexOf(bannerPosition) + 1

      return BANNER_SETTINGS.CATEGORY[
        getInsideListMpuBannerSettingsKey(bannerNumber)
      ]
    },
    isVideoVisible(index) {
      const position = index + 1
      return this.videoSettings && position === VIDEO_POSITION
    },
    async fetchLoadedArticles() {
      const payload = {
        categorySlug: this.categorySlug
      }

      const articleChunks = await this.$_articleDataHandler_fetchLoadedArticles(
        {
          action: this.requestNewsByCategory,
          initialPage: this.initialPage,
          defaultTakeFn: this.getDefaultTake,
          length: this.articles.length,
          payload: payload
        }
      )
      return articleChunks.reduce(
        (acc, { articles }) => [...acc, ...articles],
        []
      )
    },
    getDefaultTake() {
      return DEFAULT_TAKE
    },
    isInViewport(position) {
      return position < ARTICLE_SLOT_COUNT_IN_VIEWPORT
    },
    refreshCategoryPageContent() {
      const dataKeysForDelayedContentRefresh = Object.keys(
        generateDefaultFeedContentDataModel()
      )

      this.$_delayedContentRefresh_run({
        getOldDataFn: () => pick(dataKeysForDelayedContentRefresh, this),
        requestNewDataFn: async () => {
          const newData = await getFeedData({
            route: this.$route,
            store: this.$store
          })

          return pick(dataKeysForDelayedContentRefresh, newData)
        },
        dataComparisonKeysToOmitDeep: ['lastModified', 'ModifiedOn'],
        dataChangedCallbackFn: newData => {
          Object.assign(this, pick(dataKeysForDelayedContentRefresh, newData))

          const componentIdentifiers = this.articles.map(
            article => article.articleId
          )
          this.$_lazyHydratedComponentState_rerenderNonHydratedComponents([
            ...componentIdentifiers,
            ...(this.videoSettings ? [this.videoSettings.Video.ExternalId] : [])
          ])
        }
      })
    }
  },
  mounted() {
    this.refreshCategoryPageContent()
  }
}
</script>

<style lang="scss" scoped>
.category-news__wrapper {
  .category-news__title {
    $margin-bottom: (
      $above-desktop-xl: 32px,
      $tablet: 18px,
      $mobile: 20px
    );

    @include setStyleByBreakpoint('margin-bottom', $margin-bottom);
  }

  .category-news__title-wrapper {
    display: flex;
    justify-content: space-between;
  }

  .category-news__container {
    margin-top: $gap-between-page-title-and-content-card;

    @include mobile {
      margin-top: 0;
    }
  }

  .category-news__article {
    margin-bottom: 20px;
  }

  /deep/ .article-list__item-wrapper:last-child .category-news__article {
    margin-bottom: 0;
  }

  .category-news__banner {
    margin-top: 24px;
    margin-bottom: 24px;
  }

  .category-news__video-banner {
    margin: 30px 0 20px 0;

    @include mobile {
      margin-bottom: 30px;
    }
  }

  .category-news__video {
    margin: 40px 0;

    @include mobile {
      margin: 20px 0;
    }
  }

  .category-news__view-more {
    $margin: (
      $above-desktop-xl: 20px 0 60px,
      $tablet: 20px 0 40px,
      $mobile: 20px 0 24px
    );

    @include setStyleByBreakpoint('margin', $margin);
  }
}
</style>
